/**
 * UrlRow — řádek se URL pro instance karty modulu.
 */
import { Copy, ExternalLink, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

async function copyText(text: string, ok: string, fail: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(ok);
    return;
  } catch {
    /* fallback */
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    toast.success(ok);
  } catch {
    toast.error(fail);
  }
  document.body.removeChild(el);
}

export interface UrlRowProps {
  label: string;
  url: string | null | undefined;
  /** Public URL = zelená barva, Globe ikona, ExternalLink na otevření v novém tabu */
  isPublic?: boolean;
  /** Vlastní href (default = url, pro non-clickable URLs typu host:port) */
  href?: string;
}

export function UrlRow({ label, url, isPublic, href }: UrlRowProps) {
  const { t } = useTranslation();
  if (!url) return null;
  const link = href ?? (url.startsWith('http') ? url : null);
  return (
    <div className='flex items-center justify-between gap-2 text-xs'>
      <span className='text-gray-500 flex-shrink-0'>{label}</span>
      <div className='flex items-center gap-1 min-w-0'>
        {isPublic && <Globe size={11} className='text-emerald-400 flex-shrink-0' />}
        <code className={`font-mono truncate ${isPublic ? 'text-emerald-400' : 'text-plex'}`}>
          {url}
        </code>
        <button
          type='button'
          onClick={() => copyText(url, t('ui.copied'), t('ui.copyFailed'))}
          className='text-gray-600 hover:text-gray-400 flex-shrink-0 ml-1'
          title={t('ui.copy')}
        >
          <Copy size={11} />
        </button>
        {isPublic && link && (
          <a
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            className='text-gray-600 hover:text-gray-400 flex-shrink-0'
            title={t('ui.open')}
          >
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
}
